import React, { useState, useEffect } from 'react';
import { 
  Package, Plus, Edit2, Trash2, Eye, 
  Search, Filter, MoreVertical, Tag, 
  ChevronDown, Grid, List, Star,
  Download, CheckSquare, Square, Upload,
  Loader2, AlertCircle, CheckCircle, XCircle, RefreshCw,
  Image as ImageIcon, Layers, Palette, Ruler, Package2
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';

const Products = () => {
  const navigate = useNavigate();
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [attributes, setAttributes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingCategories, setLoadingCategories] = useState(false);
  const [error, setError] = useState(null);
  const [successMessage, setSuccessMessage] = useState('');
  
  const [viewMode, setViewMode] = useState('grid');
  const [showAddModal, setShowAddModal] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [selectedStatus, setSelectedStatus] = useState('all');
  const [selectedProducts, setSelectedProducts] = useState([]);
  const [showFilter, setShowFilter] = useState(false);
  
  // API configuration
  const API_BASE = 'https://api.maechi.in';
  
  // Fetch categories
  const fetchCategories = async () => {
    try {
      setLoadingCategories(true);
      const response = await fetch(`${API_BASE}/products?action=categories`);
      const data = await response.json();
      
      if (data.success) {
        setCategories(data.data || []);
      }
    } catch (err) {
      console.error('Error fetching categories:', err);
    } finally {
      setLoadingCategories(false);
    }
  };
  
  // Fetch attributes
  const fetchAttributes = async () => {
    try {
      const response = await fetch(`${API_BASE}/products?action=attributes`);
      const data = await response.json();
      
      if (data.success) {
        setAttributes(data.data || []);
      }
    } catch (err) {
      console.error('Error fetching attributes:', err);
    }
  };
  
  // Fetch products from API using fetch
  const fetchProducts = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const params = new URLSearchParams();
      if (searchQuery) params.append('search', searchQuery);
      if (selectedCategory !== 'all') params.append('category', selectedCategory);
      if (selectedStatus !== 'all') params.append('status', selectedStatus);
      
      const response = await fetch(`${API_BASE}/products?${params}`);
      
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      
      const data = await response.json();
      
      if (data.success) {
        setProducts(data.data || []);
      } else {
        setError(data.message || 'Failed to fetch products');
      }
    } catch (err) {
      console.error('API Error:', err);
      setError(err.message || 'Network error');
    } finally {
      setLoading(false);
    }
  };

  // Initial fetches
  useEffect(() => {
    fetchProducts();
    fetchCategories();
    fetchAttributes();
  }, [searchQuery, selectedCategory, selectedStatus]);

  // Handle delete product with fetch
  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this product?')) return;
    
    try {
      const response = await fetch(`${API_BASE}/products?id=${id}`, {
        method: 'DELETE'
      });
      
      const data = await response.json();
      
      if (data.success) {
        // Remove from local state
        setProducts(products.filter(p => p.id !== id));
        setSelectedProducts(selectedProducts.filter(productId => productId !== id));
        setSuccessMessage('Product deleted successfully');
        setTimeout(() => setSuccessMessage(''), 3000);
      } else {
        setError(data.message || 'Failed to delete product');
      }
    } catch (err) {
      console.error('Delete Error:', err);
      setError('Failed to delete product');
    }
  };

  // Toggle featured status with fetch
  const toggleFeatured = async (id) => {
    try {
      const product = products.find(p => p.id === id);
      const newFeaturedStatus = !product.featured;
      
      const response = await fetch(`${API_BASE}/products?id=${id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ featured: newFeaturedStatus ? 1 : 0 })
      });
      
      const data = await response.json();
      
      if (data.success) {
        // Update local state
        setProducts(products.map(p => 
          p.id === id ? { ...p, featured: newFeaturedStatus } : p
        ));
        setSuccessMessage(`Product ${newFeaturedStatus ? 'marked as featured' : 'removed from featured'}`);
        setTimeout(() => setSuccessMessage(''), 3000);
      } else {
        setError(data.message || 'Failed to update product');
      }
    } catch (err) {
      console.error('Update Error:', err);
      setError('Failed to update product status');
    }
  };

  // Handle add product with fetch
  const handleAddProduct = async (productData) => {
    try {
      const response = await fetch(`${API_BASE}/products`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(productData)
      });
      
      const data = await response.json();
      
      if (data.success) {
        fetchProducts(); // Refresh the list
        setShowAddModal(false);
        setSuccessMessage('Product added successfully');
        setTimeout(() => setSuccessMessage(''), 3000);
      } else {
        setError(data.message || 'Failed to add product');
      }
    } catch (err) {
      console.error('Add Error:', err);
      setError(err.message || 'Failed to add product');
    }
  };

  // Bulk delete with fetch
  const handleBulkDelete = async () => {
    if (selectedProducts.length === 0) return;
    
    if (!window.confirm(`Delete ${selectedProducts.length} selected products?`)) return;
    
    try {
      const deletePromises = selectedProducts.map(id => 
        fetch(`${API_BASE}/products?id=${id}`, {
          method: 'DELETE'
        }).then(res => res.json())
      );
      
      const results = await Promise.allSettled(deletePromises);
      
      const failedDeletes = results.filter(result => 
        result.status === 'rejected' || 
        (result.status === 'fulfilled' && !result.value.success)
      );
      
      if (failedDeletes.length === 0) {
        setProducts(products.filter(p => !selectedProducts.includes(p.id)));
        setSelectedProducts([]);
        setSuccessMessage('Selected products deleted successfully');
        setTimeout(() => setSuccessMessage(''), 3000);
      } else {
        setError(`Failed to delete ${failedDeletes.length} product(s)`);
      }
    } catch (err) {
      console.error('Bulk Delete Error:', err);
      setError('Failed to delete products');
    }
  };

  // Bulk status update
  const handleBulkStatus = async (status) => {
    if (selectedProducts.length === 0) return;
    
    try {
      const updatePromises = selectedProducts.map(id => 
        fetch(`${API_BASE}/products?id=${id}`, {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ status })
        }).then(res => res.json())
      );
      
      const results = await Promise.allSettled(updatePromises);
      
      setProducts(products.map(p => 
        selectedProducts.includes(p.id) ? { ...p, status } : p
      ));
      
      setSuccessMessage(`Status updated for ${selectedProducts.length} product(s)`);
      setTimeout(() => setSuccessMessage(''), 3000);
    } catch (err) {
      console.error('Bulk Status Error:', err);
      setError('Failed to update product status');
    }
  };

  // Handle product form submission
  const handleFormSubmit = (e) => {
    e.preventDefault();
    
    const formData = new FormData(e.target);
    const productData = {
      name: formData.get('name'),
      sku: formData.get('sku'),
      description: formData.get('description'),
      short_description: formData.get('short_description'),
      price: parseFloat(formData.get('price')) || 0,
      compare_price: parseFloat(formData.get('compare_price')) || 0,
      cost: parseFloat(formData.get('cost')) || 0,
      stock_quantity: parseInt(formData.get('stock_quantity')) || 0,
      low_stock_threshold: parseInt(formData.get('low_stock_threshold')) || 5,
      category_id: parseInt(formData.get('category_id')) || null,
      brand: formData.get('brand'),
      weight: parseFloat(formData.get('weight')) || 0,
      dimensions: formData.get('dimensions'),
      featured: formData.get('featured') === 'on' ? 1 : 0,
      status: formData.get('status')
    };
    
    handleAddProduct(productData);
  };

  // Handle view product
  const handleViewProduct = (id) => {
    navigate(`/product/${id}`);
  };

  // Handle edit product
  const handleEditProduct = (id) => {
    navigate(`/products/edit/${id}`);
  };

  // Product selection
  const handleSelectAll = () => {
    if (selectedProducts.length === filteredProducts.length) {
      setSelectedProducts([]);
    } else {
      setSelectedProducts(filteredProducts.map(p => p.id));
    }
  };

  const handleSelectProduct = (id) => {
    if (selectedProducts.includes(id)) {
      setSelectedProducts(selectedProducts.filter(productId => productId !== id));
    } else {
      setSelectedProducts([...selectedProducts, id]);
    }
  };

  // Filtered products (client-side filtering)
  const filteredProducts = products.filter(product => {
    const matchesSearch = product.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
                         product.sku.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesCategory = selectedCategory === 'all' || 
                           (product.category_slug === selectedCategory) ||
                           (product.category_name && product.category_name.toLowerCase() === selectedCategory);
    const matchesStatus = selectedStatus === 'all' || product.status === selectedStatus;
    
    return matchesSearch && matchesCategory && matchesStatus;
  });

  // Get color attribute
  const getColorAttribute = () => {
    return attributes.find(attr => attr.slug === 'color' || attr.name.toLowerCase() === 'color');
  };

  // Get size attribute
  const getSizeAttribute = () => {
    return attributes.find(attr => attr.slug === 'size' || attr.name.toLowerCase() === 'size');
  };

  // Get material attribute
  const getMaterialAttribute = () => {
    return attributes.find(attr => attr.slug === 'material' || attr.name.toLowerCase() === 'material');
  };

  // Stats calculation
  const stats = {
    total: products.length,
    active: products.filter(p => p.status === 'active').length,
    outOfStock: products.filter(p => p.status === 'out_of_stock').length,
    featured: products.filter(p => p.featured).length,
  };

  // Loading state
  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <Loader2 className="w-12 h-12 animate-spin text-blue-600 mx-auto mb-4" />
          <p className="text-gray-600">Loading products...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Success/Error Messages */}
      {successMessage && (
        <div className="bg-green-50 border border-green-200 rounded-lg p-4 flex items-center">
          <CheckCircle className="w-5 h-5 text-green-600 mr-3" />
          <span className="text-green-800">{successMessage}</span>
          <button onClick={() => setSuccessMessage('')} className="ml-auto">
            <XCircle className="w-4 h-4 text-green-600" />
          </button>
        </div>
      )}
      
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 flex items-center">
          <AlertCircle className="w-5 h-5 text-red-600 mr-3" />
          <span className="text-red-800">{error}</span>
          <button onClick={() => setError('')} className="ml-auto">
            <XCircle className="w-4 h-4 text-red-600" />
          </button>
        </div>
      )}

      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Products</h1>
          <p className="text-gray-600">Manage your product inventory ({filteredProducts.length} products)</p>
        </div>
        <div className="flex items-center space-x-3">
          <button
            onClick={fetchProducts}
            className="px-4 py-2.5 border border-gray-300 rounded-lg hover:bg-gray-50 flex items-center"
          >
            <RefreshCw size={18} className="mr-2" />
            Refresh
          </button>
          <button className="px-4 py-2.5 border border-gray-300 rounded-lg hover:bg-gray-50 flex items-center">
            <Download size={18} className="mr-2" />
            Export
          </button>
          <button
            onClick={() => setShowAddModal(true)}
            className="px-4 py-2.5 bg-blue-600 text-white rounded-lg hover:bg-blue-700 flex items-center"
          >
            <Plus size={18} className="mr-2" />
            Add Product
          </button>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <div className="bg-white p-5 rounded-xl border border-gray-200">
          <div className="flex items-center">
            <div className="p-3 bg-blue-50 rounded-lg mr-4">
              <Package className="text-blue-600" size={24} />
            </div>
            <div>
              <p className="text-gray-500 text-sm">Total Products</p>
              <p className="text-2xl font-bold text-gray-900">{stats.total}</p>
            </div>
          </div>
        </div>
        
        <div className="bg-white p-5 rounded-xl border border-gray-200">
          <div className="flex items-center">
            <div className="p-3 bg-green-50 rounded-lg mr-4">
              <Package className="text-green-600" size={24} />
            </div>
            <div>
              <p className="text-gray-500 text-sm">Active</p>
              <p className="text-2xl font-bold text-gray-900">{stats.active}</p>
            </div>
          </div>
        </div>
        
        <div className="bg-white p-5 rounded-xl border border-gray-200">
          <div className="flex items-center">
            <div className="p-3 bg-red-50 rounded-lg mr-4">
              <Package className="text-red-600" size={24} />
            </div>
            <div>
              <p className="text-gray-500 text-sm">Out of Stock</p>
              <p className="text-2xl font-bold text-gray-900">{stats.outOfStock}</p>
            </div>
          </div>
        </div>
        
        <div className="bg-white p-5 rounded-xl border border-gray-200">
          <div className="flex items-center">
            <div className="p-3 bg-yellow-50 rounded-lg mr-4">
              <Star className="text-yellow-600" size={24} />
            </div>
            <div>
              <p className="text-gray-500 text-sm">Featured</p>
              <p className="text-2xl font-bold text-gray-900">{stats.featured}</p>
            </div>
          </div>
        </div>
      </div>

      {/* Bulk Actions */}
      {selectedProducts.length > 0 && (
        <div className="bg-blue-50 border border-blue-200 rounded-xl p-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center">
              <span className="font-medium text-blue-800">
                {selectedProducts.length} product(s) selected
              </span>
            </div>
            <div className="flex items-center space-x-3">
              <button
                onClick={() => handleBulkStatus('active')}
                className="px-3 py-1.5 bg-green-100 text-green-800 rounded-lg text-sm hover:bg-green-200"
              >
                Set Active
              </button>
              <button
                onClick={() => handleBulkStatus('out_of_stock')}
                className="px-3 py-1.5 bg-red-100 text-red-800 rounded-lg text-sm hover:bg-red-200"
              >
                Set Out of Stock
              </button>
              <button
                onClick={handleBulkDelete}
                className="px-3 py-1.5 bg-red-600 text-white rounded-lg text-sm hover:bg-red-700"
              >
                Delete Selected
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Controls */}
      <div className="bg-white p-4 rounded-xl border border-gray-200">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-4 flex-1">
            <div className="relative flex-1 max-w-md">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={20} />
              <input
                type="text"
                placeholder="Search products by name, SKU, category..."
                className="w-full pl-10 pr-4 py-2.5 bg-gray-50 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
              />
            </div>
            
            <div className="relative">
              <button 
                onClick={() => setShowFilter(!showFilter)}
                className="px-4 py-2.5 border border-gray-300 rounded-lg flex items-center hover:bg-gray-50"
              >
                <Filter size={18} className="mr-2" />
                Filter
                <ChevronDown size={16} className="ml-2" />
              </button>
              
              {/* Filter Dropdown */}
              {showFilter && (
                <div className="absolute top-full left-0 mt-2 w-64 bg-white border border-gray-200 rounded-xl shadow-lg z-10 p-4">
                  <div className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Category
                      </label>
                      <select
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                        value={selectedCategory}
                        onChange={(e) => setSelectedCategory(e.target.value)}
                      >
                        <option value="all">All Categories</option>
                        {categories.map(cat => (
                          <option key={cat.id} value={cat.slug || cat.name.toLowerCase()}>
                            {cat.name}
                          </option>
                        ))}
                      </select>
                    </div>
                    
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Status
                      </label>
                      <select
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                        value={selectedStatus}
                        onChange={(e) => setSelectedStatus(e.target.value)}
                      >
                        <option value="all">All Status</option>
                        <option value="active">Active</option>
                        <option value="draft">Draft</option>
                        <option value="out_of_stock">Out of Stock</option>
                      </select>
                    </div>
                    
                    <div className="pt-2 border-t border-gray-200">
                      <button
                        onClick={() => {
                          setSelectedCategory('all');
                          setSelectedStatus('all');
                          setShowFilter(false);
                        }}
                        className="w-full px-3 py-2 text-sm text-gray-600 hover:bg-gray-100 rounded-lg"
                      >
                        Clear Filters
                      </button>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
          
          <div className="flex items-center space-x-3">
            <div className="flex border border-gray-300 rounded-lg overflow-hidden">
              <button
                onClick={() => setViewMode('grid')}
                className={`px-3 py-2 ${viewMode === 'grid' ? 'bg-blue-50 text-blue-600' : 'text-gray-600'}`}
              >
                <Grid size={20} />
              </button>
              <button
                onClick={() => setViewMode('list')}
                className={`px-3 py-2 ${viewMode === 'list' ? 'bg-blue-50 text-blue-600' : 'text-gray-600'}`}
              >
                <List size={20} />
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Products Count & Select All */}
      <div className="flex items-center justify-between">
        <div className="text-sm text-gray-600">
          Showing {filteredProducts.length} of {products.length} products
        </div>
        {viewMode === 'list' && filteredProducts.length > 0 && (
          <button
            onClick={handleSelectAll}
            className="flex items-center text-sm text-gray-600 hover:text-gray-900"
          >
            {selectedProducts.length === filteredProducts.length ? (
              <CheckSquare size={16} className="mr-2" />
            ) : (
              <Square size={16} className="mr-2" />
            )}
            Select All
          </button>
        )}
      </div>

      {/* Products Grid/List */}
      {filteredProducts.length === 0 ? (
        <div className="text-center py-16 bg-white rounded-xl border border-gray-200">
          <Package className="mx-auto text-gray-300" size={48} />
          <h3 className="text-lg font-medium text-gray-900 mt-4">No products found</h3>
          <p className="text-gray-600 mt-2">Try changing your search or filter criteria</p>
          <button
            onClick={() => {
              setSearchQuery('');
              setSelectedCategory('all');
              setSelectedStatus('all');
            }}
            className="mt-4 px-4 py-2 text-blue-600 hover:bg-blue-50 rounded-lg"
          >
            Clear all filters
          </button>
        </div>
      ) : viewMode === 'grid' ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {filteredProducts.map((product) => (
            <div key={product.id} className="bg-white rounded-xl border border-gray-200 overflow-hidden hover:shadow-lg transition-shadow">
              <div className="relative h-48 bg-gray-100 overflow-hidden">
                <img 
                  src={product.image} 
                  alt={product.name}
                  className="w-full h-full object-cover"
                  onError={(e) => {
                    e.target.src = 'https://images.unsplash.com/photo-1553062407-98eeb64c6a62?w=400';
                  }}
                />
                {product.featured && (
                  <div className="absolute top-3 left-3">
                    <span className="px-2 py-1 bg-yellow-500 text-white text-xs font-bold rounded">
                      Featured
                    </span>
                  </div>
                )}
                {product.colors && product.colors.length > 0 && (
                  <div className="absolute top-3 right-3 flex space-x-1">
                    {product.colors.slice(0, 3).map((color, idx) => (
                      <div 
                        key={idx}
                        className="w-6 h-6 rounded-full border-2 border-white shadow"
                        style={{ backgroundColor: color }}
                        title={`Color ${idx + 1}`}
                      />
                    ))}
                  </div>
                )}
              </div>
              
              <div className="p-5">
                <div className="flex justify-between items-start mb-2">
                  <h3 className="font-bold text-gray-900 text-lg">{product.name}</h3>
                  <button 
                    onClick={() => toggleFeatured(product.id)}
                    className={`p-1 rounded ${product.featured ? 'text-yellow-500' : 'text-gray-300 hover:text-yellow-400'}`}
                  >
                    <Star size={18} fill={product.featured ? "currentColor" : "none"} />
                  </button>
                </div>
                
                <p className="text-gray-500 text-sm mb-3">SKU: {product.sku}</p>
                
                <div className="flex items-center justify-between mb-4">
                  <div>
                    <span className="text-2xl font-bold text-gray-900">₹{product.price}</span>
                    {product.compare_price > product.price && (
                      <span className="ml-2 text-sm text-gray-500 line-through">₹{product.compare_price}</span>
                    )}
                  </div>
                  <span className={`px-3 py-1 rounded-full text-xs font-medium ${
                    product.status === 'active' 
                      ? 'bg-green-100 text-green-800' 
                      : product.status === 'out_of_stock'
                      ? 'bg-red-100 text-red-800'
                      : 'bg-gray-100 text-gray-800'
                  }`}>
                    {product.status === 'out_of_stock' ? 'Out of Stock' : 
                     product.status === 'draft' ? 'Draft' : 'In Stock'}
                  </span>
                </div>
                
                <div className="flex items-center justify-between text-sm text-gray-600 mb-2">
                  <span className="flex items-center">
                    <Package2 size={14} className="mr-1" />
                    {product.category_name || 'Uncategorized'}
                  </span>
                  <span className={`font-medium ${product.stock_quantity < 10 ? 'text-red-600' : 'text-gray-900'}`}>
                    Stock: {product.stock_quantity}
                  </span>
                </div>
                
                {product.variant_count > 0 && (
                  <div className="text-xs text-blue-600 mb-3">
                    <Layers size={12} className="inline mr-1" />
                    {product.variant_count} variant(s)
                  </div>
                )}
                
                <div className="flex items-center space-x-3 mt-5 pt-4 border-t border-gray-100">
                  <button 
                    onClick={() => handleViewProduct(product.id)}
                    className="flex-1 px-3 py-2 text-blue-600 hover:bg-blue-50 rounded-lg flex items-center justify-center"
                  >
                    <Eye size={16} className="mr-2" />
                    View
                  </button>
                  <button 
                    onClick={() => handleEditProduct(product.id)}
                    className="flex-1 px-3 py-2 text-green-600 hover:bg-green-50 rounded-lg flex items-center justify-center"
                  >
                    <Edit2 size={16} className="mr-2" />
                    Edit
                  </button>
                  <button 
                    onClick={() => handleDelete(product.id)}
                    className="flex-1 px-3 py-2 text-red-600 hover:bg-red-50 rounded-lg flex items-center justify-center"
                  >
                    <Trash2 size={16} className="mr-2" />
                    Delete
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      ) : (
        /* List View with Checkboxes */
        <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50">
                <tr>
                  <th className="text-left p-4">
                    <button onClick={handleSelectAll} className="flex items-center">
                      {selectedProducts.length === filteredProducts.length ? (
                        <CheckSquare size={16} />
                      ) : (
                        <Square size={16} />
                      )}
                    </button>
                  </th>
                  <th className="text-left p-4 text-sm font-semibold text-gray-700">Product</th>
                  <th className="text-left p-4 text-sm font-semibold text-gray-700">SKU</th>
                  <th className="text-left p-4 text-sm font-semibold text-gray-700">Category</th>
                  <th className="text-left p-4 text-sm font-semibold text-gray-700">Price</th>
                  <th className="text-left p-4 text-sm font-semibold text-gray-700">Stock</th>
                  <th className="text-left p-4 text-sm font-semibold text-gray-700">Status</th>
                  <th className="text-left p-4 text-sm font-semibold text-gray-700">Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredProducts.map((product) => (
                  <tr key={product.id} className="border-t border-gray-100 hover:bg-gray-50">
                    <td className="p-4">
                      <button onClick={() => handleSelectProduct(product.id)}>
                        {selectedProducts.includes(product.id) ? (
                          <CheckSquare size={16} className="text-blue-600" />
                        ) : (
                          <Square size={16} className="text-gray-400" />
                        )}
                      </button>
                    </td>
                    <td className="p-4">
                      <div className="flex items-center">
                        <div className="w-12 h-12 bg-gray-100 rounded-lg mr-3 overflow-hidden">
                          <img 
                            src={product.image} 
                            alt={product.name} 
                            className="w-full h-full object-cover"
                            onError={(e) => {
                              e.target.src = 'https://images.unsplash.com/photo-1553062407-98eeb64c6a62?w=400';
                            }}
                          />
                        </div>
                        <div>
                          <p className="font-medium text-gray-900">{product.name}</p>
                          {product.featured && (
                            <span className="inline-flex items-center text-yellow-600 text-xs">
                              <Star size={12} className="mr-1" fill="currentColor" />
                              Featured
                            </span>
                          )}
                        </div>
                      </div>
                    </td>
                    <td className="p-4">
                      <span className="font-mono text-sm">{product.sku}</span>
                    </td>
                    <td className="p-4">
                      <span className="text-gray-700">{product.category_name || 'Uncategorized'}</span>
                    </td>
                    <td className="p-4">
                      <div>
                        <span className="font-bold text-gray-900">₹{product.price}</span>
                        {product.compare_price > product.price && (
                          <span className="ml-2 text-sm text-gray-500 line-through">₹{product.compare_price}</span>
                        )}
                      </div>
                    </td>
                    <td className="p-4">
                      <span className={`font-medium ${product.stock_quantity < 10 ? 'text-red-600' : 'text-gray-900'}`}>
                        {product.stock_quantity}
                      </span>
                    </td>
                    <td className="p-4">
                      <span className={`px-3 py-1 rounded-full text-xs font-medium ${
                        product.status === 'active' 
                          ? 'bg-green-100 text-green-800' 
                          : product.status === 'out_of_stock'
                          ? 'bg-red-100 text-red-800'
                          : 'bg-gray-100 text-gray-800'
                      }`}>
                        {product.status === 'out_of_stock' ? 'Out of Stock' : 
                         product.status === 'draft' ? 'Draft' : 'In Stock'}
                      </span>
                    </td>
                    <td className="p-4">
                      <div className="flex items-center space-x-3">
                        <button 
                          onClick={() => handleViewProduct(product.id)}
                          className="text-blue-600 hover:text-blue-800"
                        >
                          <Eye size={18} />
                        </button>
                        <button 
                          onClick={() => handleEditProduct(product.id)}
                          className="text-green-600 hover:text-green-800"
                        >
                          <Edit2 size={18} />
                        </button>
                        <button 
                          onClick={() => handleDelete(product.id)}
                          className="text-red-600 hover:text-red-800"
                        >
                          <Trash2 size={18} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Pagination */}
      {filteredProducts.length > 0 && (
        <div className="flex items-center justify-between bg-white p-4 rounded-xl border border-gray-200">
          <div className="text-sm text-gray-600">
            Page 1 of 1 • {filteredProducts.length} products
          </div>
          <div className="flex items-center space-x-2">
            <button className="px-3 py-1.5 border border-gray-300 rounded-lg text-sm hover:bg-gray-50">
              Previous
            </button>
            <button className="px-3 py-1.5 bg-blue-600 text-white rounded-lg text-sm hover:bg-blue-700">
              1
            </button>
            <button className="px-3 py-1.5 border border-gray-300 rounded-lg text-sm hover:bg-gray-50">
              Next
            </button>
          </div>
        </div>
      )}

      {/* Add Product Modal */}
      {showAddModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-2xl w-full max-w-4xl max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b border-gray-200 sticky top-0 bg-white">
              <div className="flex justify-between items-center">
                <div>
                  <h3 className="text-xl font-bold text-gray-900">Add New Product</h3>
                  <p className="text-gray-600 text-sm mt-1">Create a new product listing</p>
                </div>
                <button
                  onClick={() => setShowAddModal(false)}
                  className="text-gray-400 hover:text-gray-600"
                >
                  ✕
                </button>
              </div>
            </div>
            
            <div className="p-6">
              <form onSubmit={handleFormSubmit}>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  {/* Basic Information */}
                  <div className="space-y-4">
                    <h4 className="font-medium text-gray-900 flex items-center">
                      <Package2 className="mr-2" size={20} />
                      Basic Information
                    </h4>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Product Name *
                      </label>
                      <input
                        type="text"
                        name="name"
                        required
                        className="w-full px-4 py-2.5 border border-gray-300 rounded-lg"
                        placeholder="Enter product name"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        SKU (Stock Keeping Unit) *
                      </label>
                      <input
                        type="text"
                        name="sku"
                        required
                        className="w-full px-4 py-2.5 border border-gray-300 rounded-lg"
                        placeholder="e.g., MAE-BP-001"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Short Description
                      </label>
                      <textarea
                        name="short_description"
                        rows="2"
                        className="w-full px-4 py-2.5 border border-gray-300 rounded-lg"
                        placeholder="Brief product description..."
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Category
                      </label>
                      <select
                        name="category_id"
                        className="w-full px-4 py-2.5 border border-gray-300 rounded-lg"
                      >
                        <option value="">Select Category</option>
                        {categories.map(cat => (
                          <option key={cat.id} value={cat.id}>
                            {cat.name}
                          </option>
                        ))}
                      </select>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Brand
                      </label>
                      <input
                        type="text"
                        name="brand"
                        className="w-full px-4 py-2.5 border border-gray-300 rounded-lg"
                        placeholder="Brand name"
                      />
                    </div>
                  </div>
                  
                  {/* Pricing & Inventory */}
                  <div className="space-y-4">
                    <h4 className="font-medium text-gray-900 flex items-center">
                      <Tag className="mr-2" size={20} />
                      Pricing & Inventory
                    </h4>
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Price (₹) *
                        </label>
                        <input
                          type="number"
                          name="price"
                          required
                          step="0.01"
                          min="0"
                          className="w-full px-4 py-2.5 border border-gray-300 rounded-lg"
                          placeholder="0.00"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Compare Price
                        </label>
                        <input
                          type="number"
                          name="compare_price"
                          step="0.01"
                          min="0"
                          className="w-full px-4 py-2.5 border border-gray-300 rounded-lg"
                          placeholder="0.00"
                        />
                      </div>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Cost Price
                      </label>
                      <input
                        type="number"
                        name="cost"
                        step="0.01"
                        min="0"
                        className="w-full px-4 py-2.5 border border-gray-300 rounded-lg"
                        placeholder="Product cost"
                      />
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Stock Quantity
                        </label>
                        <input
                          type="number"
                          name="stock_quantity"
                          min="0"
                          className="w-full px-4 py-2.5 border border-gray-300 rounded-lg"
                          placeholder="0"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Low Stock Alert
                        </label>
                        <input
                          type="number"
                          name="low_stock_threshold"
                          min="0"
                          defaultValue="5"
                          className="w-full px-4 py-2.5 border border-gray-300 rounded-lg"
                          placeholder="5"
                        />
                      </div>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Status
                      </label>
                      <select name="status" className="w-full px-4 py-2.5 border border-gray-300 rounded-lg">
                        <option value="active">Active</option>
                        <option value="draft">Draft</option>
                        <option value="out_of_stock">Out of Stock</option>
                      </select>
                    </div>
                    <div className="flex items-center">
                      <input
                        type="checkbox"
                        id="featured"
                        name="featured"
                        className="h-4 w-4 text-blue-600 border-gray-300 rounded"
                      />
                      <label htmlFor="featured" className="ml-2 block text-sm text-gray-900">
                        Mark as Featured Product
                      </label>
                    </div>
                  </div>
                  
                  {/* Description */}
                  <div className="md:col-span-2">
                    <h4 className="font-medium text-gray-900 mb-4">Description</h4>
                    <textarea
                      rows="4"
                      name="description"
                      className="w-full px-4 py-2.5 border border-gray-300 rounded-lg"
                      placeholder="Enter detailed product description..."
                    />
                  </div>
                  
                  {/* Product Attributes */}
                  <div className="md:col-span-2">
                    <h4 className="font-medium text-gray-900 mb-4 flex items-center">
                      <Layers className="mr-2" size={20} />
                      Product Attributes
                    </h4>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                      {/* Colors */}
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Colors
                        </label>
                        <div className="flex flex-wrap gap-2">
                          {getColorAttribute()?.values?.map(color => (
                            <div
                              key={color.id}
                              className="w-8 h-8 rounded-full border-2 border-gray-300 cursor-pointer"
                              style={{ backgroundColor: color.hex_code || '#ccc' }}
                              title={color.value}
                            />
                          )) || (
                            <>
                              <div className="w-8 h-8 rounded-full border-2 border-gray-300 cursor-pointer bg-black" title="Black" />
                              <div className="w-8 h-8 rounded-full border-2 border-gray-300 cursor-pointer bg-blue-500" title="Blue" />
                              <div className="w-8 h-8 rounded-full border-2 border-gray-300 cursor-pointer bg-red-500" title="Red" />
                              <div className="w-8 h-8 rounded-full border-2 border-gray-300 cursor-pointer bg-green-500" title="Green" />
                            </>
                          )}
                          <button type="button" className="w-8 h-8 rounded-full border-2 border-dashed border-gray-300 flex items-center justify-center text-gray-400 hover:text-gray-600">
                            +
                          </button>
                        </div>
                      </div>
                      
                      {/* Sizes */}
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Sizes
                        </label>
                        <select className="w-full px-4 py-2.5 border border-gray-300 rounded-lg">
                          <option>Select Size</option>
                          {getSizeAttribute()?.values?.map(size => (
                            <option key={size.id} value={size.value}>
                              {size.value}
                            </option>
                          )) || (
                            <>
                              <option>Small</option>
                              <option>Medium</option>
                              <option>Large</option>
                            </>
                          )}
                        </select>
                      </div>
                      
                      {/* Material */}
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Material
                        </label>
                        <select className="w-full px-4 py-2.5 border border-gray-300 rounded-lg">
                          <option>Select Material</option>
                          {getMaterialAttribute()?.values?.map(material => (
                            <option key={material.id} value={material.value}>
                              {material.value}
                            </option>
                          )) || (
                            <>
                              <option>Polycarbonate</option>
                              <option>Nylon</option>
                              <option>Leather</option>
                            </>
                          )}
                        </select>
                      </div>
                    </div>
                  </div>
                  
                  {/* Dimensions & Weight */}
                  <div className="md:col-span-2">
                    <h4 className="font-medium text-gray-900 mb-4 flex items-center">
                      <Ruler className="mr-2" size={20} />
                      Dimensions & Weight
                    </h4>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Weight (kg)
                        </label>
                        <input
                          type="number"
                          name="weight"
                          step="0.01"
                          min="0"
                          className="w-full px-4 py-2.5 border border-gray-300 rounded-lg"
                          placeholder="0.00"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Dimensions (L×W×H)
                        </label>
                        <input
                          type="text"
                          name="dimensions"
                          className="w-full px-4 py-2.5 border border-gray-300 rounded-lg"
                          placeholder="e.g., 30×20×50 cm"
                        />
                      </div>
                    </div>
                  </div>
                </div>
                
                {/* Form Actions */}
                <div className="flex justify-end space-x-3 mt-8 pt-6 border-t border-gray-200">
                  <button
                    type="button"
                    onClick={() => setShowAddModal(false)}
                    className="px-6 py-2.5 border border-gray-300 rounded-lg hover:bg-gray-50"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="px-6 py-2.5 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                  >
                    Create Product
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Products;